import React from "react";
import {
  Box, Stack, Typography, TextField, Select, MenuItem, Slider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { END_TYPES } from "../../constants/recurrenceConstants";
import PatternPicker from "./PatternPicker";

const T = {
  border: "#E2E8F0", textSub: "#475569", textMuted: "#94A3B8",
  primary: "#0D9488", bg: "#F1F5F9",
};

const StepRecurrence = ({ values, set, setMany, errors }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Stack spacing={3.5}>
      {/* Pattern */}
      <PatternPicker values={values} set={set} setMany={setMany} errors={errors} />

      {/* Start date */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Starts on <span style={{ color: "#EF4444" }}>*</span>
        </Typography>
        <DatePicker
          value={values.recurringStartDate ? dayjs(values.recurringStartDate) : null}
          onChange={(d) => set("recurringStartDate", d ? d.format("YYYY-MM-DD") : null)}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              error: !!errors.recurringStartDate,
              helperText: errors.recurringStartDate,
              sx: { width: 200 },
            },
          }}
        />
      </Box>

      {/* End condition */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 1 }}>
          Ends
        </Typography>
        <Stack direction="row" gap={1.5} alignItems="flex-start" flexWrap="wrap">
          {END_TYPES.map(({ value, label }) => {
            const active = values.recurrenceEndType === value;
            return (
              <Box
                key={value}
                onClick={() => set("recurrenceEndType", value)}
                sx={{
                  px: "14px", py: "7px",
                  borderRadius: "9px",
                  border: `1.5px solid ${active ? T.primary : T.border}`,
                  bgcolor: active ? "#F0FDFA" : "#fff",
                  color:   active ? T.primary  : T.textSub,
                  fontWeight: active ? 700 : 500,
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: T.primary },
                  userSelect: "none",
                }}
              >
                {label}
              </Box>
            );
          })}
        </Stack>

        {/* End date picker */}
        {values.recurrenceEndType === "on" && (
          <Box sx={{ mt: 1.5 }}>
            <DatePicker
              value={values.recurrenceEndDate ? dayjs(values.recurrenceEndDate) : null}
              minDate={dayjs(values.recurringStartDate).add(1, "day")}
              onChange={(d) => set("recurrenceEndDate", d ? d.format("YYYY-MM-DD") : null)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  error: !!errors.recurrenceEndDate,
                  helperText: errors.recurrenceEndDate,
                  sx: { width: 200 },
                },
              }}
            />
          </Box>
        )}

        {/* After N occurrences */}
        {values.recurrenceEndType === "after" && (
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mt: 1.5 }}>
            <TextField
              type="number"
              size="small"
              value={values.recurrenceEndAfter || ""}
              onChange={(e) => set("recurrenceEndAfter", parseInt(e.target.value) || "")}
              inputProps={{ min: 1 }}
              error={!!errors.recurrenceEndAfter}
              helperText={errors.recurrenceEndAfter}
              sx={{ width: 80 }}
            />
            <Typography sx={{ fontSize: "13px", color: T.textSub }}>
              occurrence{values.recurrenceEndAfter !== 1 ? "s" : ""}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Generation window */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.8}>
          <Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub }}>
              Generation window
            </Typography>
            <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: "2px" }}>
              Pre-generate tasks this many days ahead so they appear on the board early
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 0.4, borderRadius: "8px", bgcolor: "#F0FDFA", border: `1px solid ${T.primary}33`, minWidth: 54, textAlign: "center" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.primary }}>
              {values.generationWindow} days
            </Typography>
          </Box>
        </Stack>
        <Slider
          value={values.generationWindow}
          onChange={(_, v) => set("generationWindow", v)}
          min={7}
          max={60}
          step={1}
          marks={[
            { value: 7,  label: "7d"  },
            { value: 14, label: "14d" },
            { value: 30, label: "30d" },
            { value: 60, label: "60d" },
          ]}
          sx={{
            color: T.primary,
            "& .MuiSlider-markLabel": { fontSize: "11px", color: T.textMuted },
            "& .MuiSlider-mark": { bgcolor: T.border },
          }}
        />
      </Box>

      {/* Detected timezone (read-only info) */}
      <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: T.bg, border: `1px solid ${T.border}` }}>
        <Typography sx={{ fontSize: "11px", color: T.textMuted }}>
          🌐 Timezone detected from your browser:{" "}
          <strong style={{ color: T.textSub }}>{values.recurrenceTimezone}</strong>
        </Typography>
      </Box>
    </Stack>
  </LocalizationProvider>
);

export default StepRecurrence;
